import React, { useState, useEffect, useRef, useCallback } from 'react';

const AudioRecorder = ({ interviewId, userId }) => {
  const [micAllowed, setMicAllowed] = useState(true);
  
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const isRecordingRef = useRef(false);
  const previousIdRef = useRef(null);
  const chunkIntervalRef = useRef(null); 

  // 🔥 Background Audit Trigger Function
  const triggerInterviewAudit = useCallback(async (targetInterviewId) => {
    if (!targetInterviewId) return;

    const userPrompt = `Analyze the recording at ${process.env.REACT_APP_API_URL}/api/recordings/${targetInterviewId}. 

CRITICAL DIARIZATION RULE:
- The recruiter is always the person who controls the call agenda, asks the screening questions, and introduces the company. 
- The candidate is the person answering the questions and providing personal background. 
Carefully map and label the turns as "Recruiter:" and "Candidate:" based on their roles and context, ensuring they do not get mixed up.

Return your response strictly as a valid JSON object with the following three fields:
1. "transcript": The complete text of the conversation with turns accurately labeled as "Recruiter:" and "Candidate:".
2. "summary": A single-sentence summary focusing exclusively on the final outcome or result of the call AND explicitly stating what happens next (e.g., candidate agreed to a technical round on Tuesday, offer sent via email pending review, or call ended with no further steps).
3. "report": A detailed QA compliance and evaluation report based on the checklist.

Strict formatting constraints:
- Do NOT include any conversational filler, greetings, or explanations outside the JSON.
- Do NOT wrap the JSON in markdown code blocks or quotes.
- If the transcript is not found or empty, return empty strings for the fields.`;

    try {
      console.log(`Triggering background audit orchestration for interview: ${targetInterviewId}`);
      
      const orchestrateRes = await fetch(`${process.env.REACT_APP_API_URL}/api/orchestrate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "authtoken": localStorage.getItem("loggedintoken")
        },
        body: JSON.stringify({ prompt: userPrompt, interviewId: targetInterviewId })
      });

      if (!orchestrateRes.ok) throw new Error("Orchestration failed");
      const auditData = await orchestrateRes.json();

      console.log("🔥 RAW AUDIT DATA:", auditData);
      console.log("🔥 RAW SUMMARY STRING:", auditData.summary);

      let parsedPayload;
      
      if (typeof auditData.summary === 'object') {
          parsedPayload = auditData.summary;
      } 
      else if (typeof auditData.summary === 'string') {
          let cleanString = auditData.summary.replace(/```json/ig, '').replace(/```/g, '').trim();
          
          try {
              parsedPayload = JSON.parse(cleanString);
          } catch (e) {
              try {
                  cleanString = cleanString
                      .replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t')
                      .replace(/\\n\s*"/g, '\n"').replace(/",\\n/g, '",\n')
                      .replace(/{\\n/g, '{\n').replace(/\\n}/g, '\n}')
                      .replace(/\[\\n/g, '[\n').replace(/\\n\]/g, '\n]');
                      
                  parsedPayload = JSON.parse(cleanString);
              } catch (parseError) {
                  console.error("❌ CRASH: LLM did not return valid JSON. Clean String:", cleanString);
                  throw new Error("Failed to parse LLM audit results");
              }
          }

          if (parsedPayload.report && typeof parsedPayload.report === 'string') {
              try { parsedPayload.report = JSON.parse(parsedPayload.report); } catch(err) {}
          }
          
          if (parsedPayload.report && parsedPayload.report.complianceReport) {
              parsedPayload.report = parsedPayload.report.complianceReport;
          }
      }

      console.log("✅ Successfully parsed LLM JSON! Sending to database...", parsedPayload);
      
      const patchRes = await fetch(`${process.env.REACT_APP_API_URL}/api/recordings/${targetInterviewId}/text`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "authtoken": localStorage.getItem("loggedintoken")
        },
        body: JSON.stringify(parsedPayload)
      });

      if (!patchRes.ok) throw new Error("Failed to save audit details to DB");
      console.log(`✅ Successfully completed background audit for interview: ${targetInterviewId}`);
      
    } catch (err) {
      console.error("❌ Background interview audit execution failed:", err);
    }
  }, []);

  // 1. UPDATED: Returns the upload promise to support sequencing
  const uploadAudio = useCallback(async (audioBlob, targetId, startTime, endTime, isEmergencyUnload = false) => {
    if (!audioBlob || audioBlob.size === 0 || !targetId || !userId) return;

    const formData = new FormData();
    formData.append('audio', audioBlob, `recording_${targetId}_${Date.now()}.webm`);
    formData.append('interviewId', targetId); 
    formData.append('userId', userId); 
    formData.append('chunkStartTime', startTime);
    formData.append('chunkEndTime', endTime);

    const uploadUrl = `${process.env.REACT_APP_API_URL}/api/recordings/upload`;

    try {
      const fetchOptions = {
        method: 'POST',
        body: formData,
        headers: {
          authtoken: localStorage.getItem("loggedintoken")
        }
      };

      if (isEmergencyUnload) {
        fetchOptions.keepalive = true;
      }

      const response = await fetch(uploadUrl, fetchOptions);
      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
      console.log(`Audio chunk for interview ${targetId} successfully sent to server.`);
      return response;
    } catch (error) {
      console.error(`Audio upload failed for ${targetId}:`, error);
      throw error; // Re-throw to inform caller that upload failed
    }
  }, [userId]);

  // 2. UPDATED: No longer calls triggerInterviewAudit directly.
  const stopRecording = useCallback((isEmergencyUnload = false, cleanupMic = false, targetIdToAudit = null) => {
    if (chunkIntervalRef.current) {
      clearInterval(chunkIntervalRef.current);
      chunkIntervalRef.current = null;
    }

    const recorder = mediaRecorderRef.current;
    
    if (recorder && recorder.state !== 'inactive') {
      // Store the pending audit ID onto the recorder so onstop knows whether to trigger it
      recorder.targetIdToAudit = targetIdToAudit;

      if (isEmergencyUnload && typeof recorder.emergencyStop === 'function') {
        recorder.emergencyStop();
      } else {
        recorder.stop(); 
      }
    }
    
    isRecordingRef.current = false;

    if (cleanupMic && streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async (idToRecord) => {
    try {
      let stream = streamRef.current;
      
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            channelCount: 1,        
            sampleRate: 16000,      
            echoCancellation: true,  
            noiseSuppression: true   
          } 
        });
        
        const audioTrack = stream.getAudioTracks()[0];

        if (!audioTrack.enabled) {
          audioTrack.enabled = true; 
        }

        if (audioTrack.muted || !audioTrack.enabled) {
          console.warn("Mic is hardware-muted or OS-blocked. Aborting recording.");
          setMicAllowed(false);
          stream.getTracks().forEach(track => track.stop());
          return; 
        } else {
          setMicAllowed(true);
          streamRef.current = stream;
        }

        audioTrack.onmute = () => setMicAllowed(false);
        audioTrack.onunmute = () => setMicAllowed(true);
      }

      const spawnRecorder = (streamToRecord, targetId) => {
        const options = { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 16000 };
        const recorderOptions = MediaRecorder.isTypeSupported(options.mimeType) ? options : { audioBitsPerSecond: 16000 };

        const mediaRecorder = new MediaRecorder(streamToRecord, recorderOptions);
        
        let localAudioChunks = [];
        const localStartTime = new Date().toISOString();

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            localAudioChunks.push(event.data);
          }
        };

        // 3. UPDATED: Async onstop guarantees upload finishes before orchestrate triggers
        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(localAudioChunks, { type: 'audio/webm;codecs=opus' });
          const chunkEndTime = new Date().toISOString();
          
          try {
            // Wait for /api/recordings/upload to finish
            await uploadAudio(audioBlob, targetId, localStartTime, chunkEndTime, false);
            
            // Trigger orchestrate only after upload resolves AND if an audit was requested
            if (mediaRecorder.targetIdToAudit) {
              triggerInterviewAudit(mediaRecorder.targetIdToAudit);
            }
          } catch (err) {
            console.error("Upload failed; skipping triggerInterviewAudit execution.", err);
          }
        };

        // 4. UPDATED: Async emergencyStop handles emergency teardown sequencing
        mediaRecorder.emergencyStop = async () => {
          mediaRecorder.requestData();
          const audioBlob = new Blob(localAudioChunks, { type: 'audio/webm;codecs=opus' });
          const chunkEndTime = new Date().toISOString();
          
          mediaRecorder.onstop = null; // Prevent dual execution
          
          try {
            await uploadAudio(audioBlob, targetId, localStartTime, chunkEndTime, true);
            
            if (mediaRecorder.targetIdToAudit) {
              triggerInterviewAudit(mediaRecorder.targetIdToAudit);
            }
          } catch (err) {
            console.error("Emergency upload failed; skipping orchestration.", err);
          } finally {
            if (mediaRecorder.state !== 'inactive') {
              mediaRecorder.stop();
            }
          }
        };

        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.start(1000);
        isRecordingRef.current = true;
      };

      spawnRecorder(streamRef.current, idToRecord);

      if (chunkIntervalRef.current) clearInterval(chunkIntervalRef.current);
      
      chunkIntervalRef.current = setInterval(() => {
        const oldRecorder = mediaRecorderRef.current;
        spawnRecorder(streamRef.current, idToRecord);
        if (oldRecorder && oldRecorder.state !== 'inactive') {
          oldRecorder.stop();
        }
      }, 30000);

    } catch (err) {
      console.error("Microphone access denied or recording error:", err);
      setMicAllowed(false);
    }
  }, [uploadAudio, triggerInterviewAudit]);

  useEffect(() => {
    const currentPrevId = previousIdRef.current;

    if (!interviewId) {
      if (isRecordingRef.current && currentPrevId) {
        stopRecording(false, false, currentPrevId);
      }
      previousIdRef.current = null;
      return;
    }

    if (interviewId !== currentPrevId) {
      if (isRecordingRef.current && currentPrevId) {
        stopRecording(false, false, currentPrevId); 
      }
      startRecording(interviewId);
      previousIdRef.current = interviewId;
    }
  }, [interviewId, startRecording, stopRecording]);

  useEffect(() => {
    const currentPrevId = previousIdRef.current;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isRecordingRef.current) {
         stopRecording(true, true, currentPrevId);
      }
    };

    const handleBeforeUnload = () => {
      if (isRecordingRef.current) {
        stopRecording(true, true, currentPrevId);
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      if (isRecordingRef.current) {
        stopRecording(true, true, currentPrevId); 
      }
      
      if (chunkIntervalRef.current) {
        clearInterval(chunkIntervalRef.current);
      }
    };
  }, [stopRecording]);

  if (!micAllowed) {
    return (
      <div style={{ padding: '10px', color: 'red', textAlign: 'center', fontSize: '12px' }}>
        Microphone access is muted or blocked. Audio will not be recorded.
      </div>
    );
  }

  return null; 
};

export default AudioRecorder;